import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';

interface RelatedPostsProps {
  currentPostId: string;
  category: string;
  tags: string[] | null;
}

interface RelatedPost {
  id: string;
  slug: string;
  title: string;
  featured_image: string | null;
  category: string;
  reading_time_minutes: number;
}

export const RelatedPosts: React.FC<RelatedPostsProps> = ({
  currentPostId,
  category,
  tags,
}) => {
  const { data: relatedPosts, isLoading } = useQuery({
    queryKey: ['related-posts', currentPostId, category],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, slug, title, featured_image, category, reading_time_minutes')
        .eq('is_published', true)
        .eq('category', category)
        .neq('id', currentPostId)
        .order('published_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      return data as RelatedPost[];
    },
    enabled: !!currentPostId && !!category,
  });

  if (isLoading) {
    return (
      <section className="mt-12 pt-8 border-t border-white/10">
        <h2 className="text-2xl font-bold font-display text-white mb-6">Related Articles</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 bg-white/10 rounded-xl" />
          ))}
        </div>
      </section>
    );
  }

  if (!relatedPosts || relatedPosts.length === 0) {
    return null;
  }

  return (
    <section className="mt-12 pt-8 border-t border-white/10">
      <h2 className="text-2xl font-bold font-display text-white mb-6">Related Articles</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {relatedPosts.map((post) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            className="group block bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-[#00C2FF]/30 hover:shadow-[0_0_20px_rgba(0,194,255,0.15)] transition-all duration-300"
          >
            {/* Image */}
            <div className="aspect-video overflow-hidden">
              {post.featured_image ? (
                <img
                  src={post.featured_image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#7000FF]/20 to-[#FF00A8]/20 flex items-center justify-center">
                  <span className="text-2xl opacity-50">📝</span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-4">
              <h3 className="text-white font-medium line-clamp-2 group-hover:text-[#00C2FF] transition-colors">
                {post.title}
              </h3>
              <div className="flex items-center justify-between mt-3">
                <span className="text-white/50 text-xs">{post.reading_time_minutes} min read</span>
                <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#00C2FF] group-hover:translate-x-1 transition-all" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};
